import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Lock, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

export const CompanyLoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message || 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-gray-50 p-4 py-8">
            {/* Back Button */}
            <div className="w-full max-w-md mb-3 md:mb-4">
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
                >
                    <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm md:text-base font-medium">Back</span>
                </button>
            </div>

            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-6 md:mb-8">
                    <div className="flex items-center justify-center space-x-2 md:space-x-3 mb-3 md:mb-4">
                        <img src="/JobVitalLogo.png" alt="JobVital" className="h-8 md:h-12 w-auto" />
                        <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                            JobVital
                        </span>
                    </div>
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Hiring Login</h2>
                    <p className="text-xs md:text-sm text-slate-500 mt-1 md:mt-2">For companies and recruiters</p>
                </div>

                {/* Card */}
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 p-6 md:p-8">
                    <form onSubmit={handleLogin} className="space-y-4 md:space-y-6">
                        {error && (
                            <div className="p-3 md:p-4 bg-red-50 border border-red-100 text-red-600 text-xs md:text-sm rounded-xl flex items-start">
                                <div className="flex-shrink-0 w-1 h-1 mt-2 rounded-full bg-red-600 mr-2" />
                                {error}
                            </div>
                        )}

                        <div className="space-y-1.5 md:space-y-2">
                            <label className="text-xs md:text-sm font-medium text-gray-700 ml-1">Work Email</label>
                            <div className="relative group">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 group-focus-within:text-slate-700 transition-colors" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-200 focus:border-slate-700 outline-none transition-all text-sm md:text-base"
                                    placeholder="recruiter@company.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5 md:space-y-2">
                            <div className="flex justify-between items-center ml-1">
                                <label className="text-xs md:text-sm font-medium text-gray-700">Password</label>
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 group-focus-within:text-slate-700 transition-colors" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-200 focus:border-slate-700 outline-none transition-all text-sm md:text-base"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-2.5 md:py-3 px-4 bg-black hover:bg-slate-900 text-white text-sm md:text-base font-semibold rounded-xl shadow-lg shadow-black/20 hover:shadow-black/30 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-100 text-center">
                        <p className="text-xs md:text-sm text-gray-600">
                            Don't have an account?{' '}
                            <Link to="/hiring-signup" className="text-slate-900 font-semibold hover:text-slate-700 transition-colors">
                                Sign up
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
